/**
 * Multi-Boolean Badge History Modal
 * Shows 24-hour history of input tags and resulting multibool states
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';

interface MultiBoolHistoryData {
  badge_info: {
    id: number;
    name: string;
    description: string;
    badge_type: string;
    site_name: string;
    plant_area_name?: string;
  };
  time_range: {
    start_time: string;
    end_time: string;
  };
  input_tags: Array<{
    tag_name: string;
    input_name: string;
    input_order: number;
    invert_value: boolean;
  }>;
  tag_histories: {
    [tagName: string]: Array<{
      timestamp: string;
      value: boolean;
      quality: string;
    }>;
  };
  state_timeline: Array<{
    timestamp: string;
    state: {
      state_name: string;
      pattern: string;
      color: string;
      icon: string;
      priority: number;
      is_alarm: boolean;
    };
    tag_values: boolean[];
  }>;
  state_definitions: Array<{
    state_name: string;
    boolean_pattern: string;
    color: string;
    icon: string;
    priority: number;
    is_alarm: boolean;
    description: string;
  }>;
}

interface MultiBoolHistoryModalProps {
  open: boolean;
  onClose: () => void;
  badgeId: number;
  badgeName: string;
}

export const MultiBoolHistoryModal: React.FC<MultiBoolHistoryModalProps> = ({
  open,
  onClose,
  badgeId,
  badgeName,
}) => {
  const [data, setData] = useState<MultiBoolHistoryData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`/api/v1/sites/multibool-badges/${badgeId}/history/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}), // Use default 24hr range
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch history: ${response.status} ${errorText}`);
      }

      const historyData = await response.json();
      setData(historyData);
    } catch (err) {
      console.error('Error fetching multibool history:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch history data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && badgeId) {
      fetchHistory();
    }
  }, [open, badgeId]);

  const prepareChartData = () => {
    if (!data) return { stateData: [], tagData: [] };

    const startTime = parseISO(data.time_range.start_time);
    const endTime = parseISO(data.time_range.end_time);

    // Prepare state timeline data
    const stateData: any[] = [];
    
    if (data.state_timeline.length > 0) {
      // Add state changes from the timeline
      data.state_timeline.forEach((statePoint, index) => {
        const time = parseISO(statePoint.timestamp);
        
        // Map state names to numeric values for visualization
        const stateIndex = data.state_definitions.findIndex(
          def => def.state_name === statePoint.state.state_name
        );
        console.log(`🔍 State "${statePoint.state.state_name}" mapped to index ${stateIndex}`);
        
        stateData.push({
          timestamp: statePoint.timestamp,
          time: time.getTime(),
          state: statePoint.state.state_name,
          stateValue: stateIndex >= 0 ? stateIndex : 0,
          color: statePoint.state.color,
          pattern: statePoint.state.pattern,
        });

        // For stable states, add an end point at the end of the time range
        if (index === data.state_timeline.length - 1) {
          stateData.push({
            timestamp: data.time_range.end_time,
            time: endTime.getTime(),
            state: statePoint.state.state_name,
            stateValue: stateIndex >= 0 ? stateIndex : 0,
            color: statePoint.state.color,
            pattern: statePoint.state.pattern,
          });
        }
      });
    } else {
      // Handle case with no state changes (stable throughout period)
      // Try to determine the stable state from tag histories or use first definition
      let stableState = data.state_definitions[0] || {
        state_name: 'Unknown',
        color: '#757575',
        boolean_pattern: '??'
      };

      // If we have tag histories with stable states, try to match them to a state definition
      if (Object.keys(data.tag_histories).length > 0) {
        // Get the stable tag values to determine the pattern
        const stableTagValues: boolean[] = [];
        data.input_tags.forEach(tagInfo => {
          const tagHistory = data.tag_histories[tagInfo.tag_name] || [];
          if (tagHistory.length > 0) {
            // Use the first (and should be only) stable state value
            let stableValue = tagHistory[0].value;
            // Apply inversion if configured
            if (tagInfo.invert_value) {
              stableValue = !stableValue;
            }
            stableTagValues.push(stableValue);
          } else {
            stableTagValues.push(false); // Default to false if no data
          }
        });

        // Create pattern from stable values
        const stablePattern = stableTagValues.map(val => val ? '1' : '0').join('');
        console.log('🔍 Stable tag values:', stableTagValues);
        console.log('🔍 Calculated pattern:', stablePattern);
        
        // Find matching state definition
        const matchingState = data.state_definitions.find(def => {
          console.log(`🔍 Checking pattern ${stablePattern} against definition ${def.boolean_pattern} (${def.state_name})`);
          return def.boolean_pattern === stablePattern;
        });

        if (matchingState) {
          console.log('✅ Found matching state:', matchingState.state_name, 'with pattern:', matchingState.boolean_pattern);
          const correctStateIndex = data.state_definitions.findIndex(def => def.state_name === matchingState.state_name);
          console.log(`🔍 State "${matchingState.state_name}" maps to correct index ${correctStateIndex}`);
          stableState = matchingState;
        } else {
          console.log('❌ No matching state found for pattern:', stablePattern);
          console.log('Available patterns:', data.state_definitions.map(def => `${def.state_name}: ${def.boolean_pattern}`));
        }
      }
      
      // Find the correct index for the stable state in the definitions array
      const stableStateIndex = data.state_definitions.findIndex(def => def.state_name === stableState.state_name);
      console.log(`🔍 Using stable state "${stableState.state_name}" at index ${stableStateIndex}`);
      
      stateData.push(
        {
          timestamp: data.time_range.start_time,
          time: startTime.getTime(),
          state: stableState.state_name,
          stateValue: stableStateIndex >= 0 ? stableStateIndex : 0,
          color: stableState.color,
          pattern: stableState.boolean_pattern,
          stable: true,
        },
        {
          timestamp: data.time_range.end_time,
          time: endTime.getTime(),
          state: stableState.state_name,
          stateValue: stableStateIndex >= 0 ? stableStateIndex : 0,
          color: stableState.color,
          pattern: stableState.boolean_pattern,
          stable: true,
        }
      );
    }

    // Prepare individual tag data for a separate chart - SIMPLE 0/1 VALUES
    const tagData: any[] = [];
    
    // Create a complete timeline with all tag states
    const allTimestamps = new Set<string>();
    
    // Add all timestamps from tag histories
    Object.values(data.tag_histories).forEach(history => {
      history.forEach(point => allTimestamps.add(point.timestamp));
    });
    
    // Always add start and end times for stable state visualization
    allTimestamps.add(data.time_range.start_time);
    allTimestamps.add(data.time_range.end_time);
    
    const sortedTimestamps = Array.from(allTimestamps).sort();
    
    // Initialize tag states from stable values or first events
    const currentTagStates = new Array(data.input_tags.length).fill(false);
    
    // Set initial states from first available data
    data.input_tags.forEach((tagInfo, tagIndex) => {
      const tagHistory = data.tag_histories[tagInfo.tag_name] || [];
      if (tagHistory.length > 0) {
        let stableValue = tagHistory[0].value;
        if (tagInfo.invert_value) {
          stableValue = !stableValue;
        }
        currentTagStates[tagIndex] = stableValue;
      }
    });
    
    sortedTimestamps.forEach(timestamp => {
      const dataPoint: any = {
        timestamp,
        time: parseISO(timestamp).getTime(),
      };
      
      // Update states based on events at this timestamp
      data.input_tags.forEach((tagInfo, tagIndex) => {
        const tagHistory = data.tag_histories[tagInfo.tag_name] || [];
        const eventAtTimestamp = tagHistory.find(p => p.timestamp === timestamp);
        
        if (eventAtTimestamp) {
          // Apply inversion if configured
          let value = eventAtTimestamp.value;
          if (tagInfo.invert_value) {
            value = !value;
          }
          currentTagStates[tagIndex] = value;
        }
        
        // Store as simple 0/1 value for all timestamps
        dataPoint[`tag_${tagIndex}`] = currentTagStates[tagIndex] ? 1 : 0;
        dataPoint[`tag_${tagIndex}_name`] = tagInfo.input_name;
      });
      
      tagData.push(dataPoint);
    });

    return { stateData, tagData };
  };

  const { stateData, tagData } = prepareChartData();

  const formatTimestamp = (timestamp: string) => {
    return format(parseISO(timestamp), 'MMM dd, HH:mm:ss');
  };

  const StateTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <Card sx={{ minWidth: 200 }}>
          <CardContent sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" gutterBottom>
              {format(new Date(label), 'MMM dd, HH:mm:ss')}
            </Typography>
            <Box sx={{ mt: 1 }}>
              <Chip
                label={dataPoint.state}
                size="small"
                sx={{
                  backgroundColor: dataPoint.color,
                  color: 'white',
                  fontWeight: 'bold',
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
              Pattern: {dataPoint.pattern}
              {dataPoint.stable && ' (Stable throughout period)'}
            </Typography>
          </CardContent>
        </Card>
      );
    }
    return null;
  };

  const TagTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const dataPoint = payload[0].payload;
      return (
        <Card sx={{ minWidth: 200 }}>
          <CardContent sx={{ p: 1.5 }}>
            <Typography variant="subtitle2" gutterBottom>
              {format(new Date(label), 'MMM dd, HH:mm:ss')}
            </Typography>
            {data?.input_tags.map((tagInfo, index) => {
              const value = dataPoint[`tag_${index}`];
              if (value !== undefined) {
                return (
                  <Typography key={index} variant="body2">
                    {tagInfo.input_name}: {value}
                  </Typography>
                );
              }
              return null;
            })}
          </CardContent>
        </Card>
      );
    }
    return null;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {badgeName} - 24 Hour History
      </DialogTitle>
      
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {data && !loading && (
          <Box>
            {/* Badge Info */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {data.badge_info.name}
                </Typography>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  {data.badge_info.description}
                </Typography>
                <Typography variant="caption">
                  Site: {data.badge_info.site_name}
                  {data.badge_info.plant_area_name && ` | Area: ${data.badge_info.plant_area_name}`}
                </Typography>
              </CardContent>
            </Card>

            {/* Input Tags Summary */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Input Tags
                </Typography>
                <Grid container spacing={1}>
                  {data.input_tags.map((tagInfo, index) => (
                    <Grid item key={index}>
                      <Chip
                        label={`${tagInfo.input_name} (${tagInfo.tag_name})`}
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* State Definitions */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  State Definitions
                </Typography>
                <Grid container spacing={1}>
                  {data.state_definitions.map((stateDef, index) => (
                    <Grid item key={index}>
                      <Chip
                        label={`${stateDef.state_name} (${stateDef.boolean_pattern})`}
                        sx={{
                          backgroundColor: stateDef.color,
                          color: 'white',
                          fontWeight: 'bold',
                        }}
                        size="small"
                      />
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>

            {/* MultiBool State Timeline */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Equipment State Timeline
                </Typography>
                
                {stateData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={stateData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="time"
                        type="number"
                        scale="time"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(time) => format(new Date(time), 'HH:mm')}
                      />
                      <YAxis
                        domain={[0, data?.state_definitions.length - 1]}
                        type="number"
                        ticks={data?.state_definitions.map((_, index) => index) || []}
                        tickFormatter={(value) => {
                          const stateIndex = Math.round(value);
                          if (data && stateIndex >= 0 && stateIndex < data.state_definitions.length) {
                            return data.state_definitions[stateIndex].state_name;
                          }
                          return '';
                        }}
                      />
                      <Tooltip content={<StateTooltip />} />
                      
                      <Line
                        type="stepAfter"
                        dataKey="stateValue"
                        stroke={stateData[0]?.color || "#d32f2f"}
                        strokeWidth={4}
                        dot={false}
                        name="Equipment State"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Alert severity="info">
                    No state changes detected - equipment stable during time period.
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Individual Tag History */}
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" gutterBottom>
                  Individual Tag History
                </Typography>
                
                {tagData.length >= 2 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={tagData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="time"
                        type="number"
                        scale="time"
                        domain={['dataMin', 'dataMax']}
                        tickFormatter={(time) => format(new Date(time), 'HH:mm')}
                      />
                      <YAxis
                        domain={[-0.2, 1.2]}
                        ticks={[0, 1]}
                        tickFormatter={(value) => value.toString()}
                        label={{ value: 'Tag Values (0/1)', angle: -90, position: 'insideLeft' }}
                      />
                      <Tooltip content={<TagTooltip />} />
                      <Legend />
                      
                      {/* Lines for each input tag */}
                      {data?.input_tags.map((tagInfo, index) => (
                        <Line
                          key={index}
                          type="stepAfter"
                          dataKey={`tag_${index}`}
                          stroke={`hsl(${(index * 137.5) % 360}, 70%, 50%)`}
                          strokeWidth={3}
                          dot={false}
                          name={tagInfo.input_name}
                          connectNulls={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Alert severity="info">
                    {tagData.length === 0 
                      ? "No tag history data available for the selected time range."
                      : "Stable tag states throughout the period - no changes to display as timeline."
                    }
                  </Alert>
                )}
              </CardContent>
            </Card>

            {/* Recent State Changes */}
            {data.state_timeline.length > 0 && (
              <Card sx={{ mt: 2 }}>
                <CardContent>
                  <Typography variant="subtitle1" gutterBottom>
                    Recent State Changes
                  </Typography>
                  <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                    {data.state_timeline.slice(-10).reverse().map((statePoint, index) => (
                      <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <Typography variant="caption" sx={{ minWidth: 120 }}>
                          {formatTimestamp(statePoint.timestamp)}
                        </Typography>
                        <Chip
                          label={statePoint.state.state_name}
                          size="small"
                          sx={{
                            backgroundColor: statePoint.state.color,
                            color: 'white',
                            fontWeight: 'bold',
                          }}
                        />
                        <Typography variant="caption" color="textSecondary">
                          Pattern: {statePoint.state.pattern}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button onClick={fetchHistory} disabled={loading}>
          Refresh
        </Button>
      </DialogActions>
    </Dialog>
  );
};
